/**
 * ============================================================================
 * Achievements — the image slot. Populated, unlike the martial-arts one.
 * ============================================================================
 *
 * Same mechanism as `src/utils/martialArtsImages.ts` and for the same reason:
 * `src/content/data/*.json` is inert data with no module graph, so a path
 * written there would never reach Vite's asset pipeline. It would ship
 * unhashed and unoptimised, or more likely 404, and it would sit outside the
 * build's own accounting — the class of thing Article V.7 exists to keep out
 * of this repository. An imported binding is content-hashed into
 * `build/assets/` like every other image on the site, and the leak sweep walks
 * it.
 *
 * ----------------------------------------------------------------------------
 * WHAT THESE FOUR FRAMES ARE, AND WHAT THEY ARE NOT
 * ----------------------------------------------------------------------------
 * They are stills captured from a television broadcast. Read before adding a
 * fifth:
 *
 *   · THEY DO NOT SHOW AN INTERVIEW. Every frame is a cutaway of three people
 *     working at a desk — no presenter, no piece to camera, no name caption.
 *     What they evidence is that a crew filmed the team at work. They do not,
 *     on their own, evidence that any individual was named, which is why the
 *     page's attribution is to the company and its team (D-069).
 *
 *   · THEY ARE ONE SHOT, NOT FOUR MOMENTS. Frames 1 and 4 are 1600x900 and
 *     frames 2 and 3 are 1920x1080; all four are the same camera setup. The
 *     page presents them as frames from one segment rather than as a gallery
 *     of separate occasions, because the second reading would be false.
 *
 *   · THE CHANNEL BUG READS "DD BANGLA". The outlet recorded in
 *     `achievements.json` is the value the owner confirmed. These alt texts
 *     describe the logo the frame actually carries rather than restating the
 *     confirmed outlet over it — a description that contradicts its own image
 *     is worse than no description.
 *
 *   · REDACTION: none was required, and that is a finding rather than an
 *     omission. Two of the three people wear identification lanyards; neither
 *     is legible at this resolution, and no document, screen, address or
 *     identifier is readable in any frame. Two of the three are colleagues
 *     rather than the site's owner, and are named nowhere on the page.
 *
 * ----------------------------------------------------------------------------
 * TO ADD A FRAME
 * ----------------------------------------------------------------------------
 *   1. Check it against the four points above; redact first if it fails one.
 *   2. Put the file in `src/assets/doordarshan/`.
 *   3. Export it from `src/assets/index.ts`.
 *   4. Add an entry below AND an entry in `achievements.json`, keyed by the
 *      same id. The two must agree in both directions — the page's join
 *      assertion checks that every declared image resolves to a binary and
 *      that no binary is orphaned, which is D-065's rule applied to pictures.
 */

import { DD_Broadcast_1, DD_Broadcast_2, DD_Broadcast_3, DD_Broadcast_4 } from "../assets";

/**
 * Image id -> imported binary.
 *
 * Keys must match an `images[].id` in `achievements.json`, which is where the
 * alt text lives. Splitting them that way is deliberate: the ALT TEXT IS
 * CONTENT and belongs in the layer Article II.2 reserves for it, while the
 * binary is a module and can only live here.
 */
export const ACHIEVEMENT_IMAGES: Record<string, string> = {
	"dd-2024-1": DD_Broadcast_1,
	"dd-2024-2": DD_Broadcast_2,
	"dd-2024-3": DD_Broadcast_3,
	"dd-2024-4": DD_Broadcast_4
};

/** The binary for an image id, or undefined if the id names nothing. */
export const achievementImage = (id: string): string | undefined => ACHIEVEMENT_IMAGES[id];

/**
 * The poster frame for the video slot while it is empty.
 *
 * A still from the segment itself, not a generic placeholder: the empty state
 * still has to say what the video would show, and the page must look complete
 * without a URL because that is the state it ships in today.
 */
export const BROADCAST_POSTER: string = DD_Broadcast_1;
